import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Card,
  CardContent,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Slider,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip
} from '@mui/material';
import {
  Add,
  Delete,
  Edit,
  ExpandMore,
  Store,
  GridView,
  ViewModule,
  Straighten
} from '@mui/icons-material';
import { useStorefront3DStore } from '../store/useStorefront3DStore';

const StoreLayoutPanel = () => {
  const { 
    storeLayout, 
    updateStoreLayout, 
    addShelf, 
    removeShelf, 
    updateShelf 
  } = useStorefront3DStore();
  
  const [selectedShelf, setSelectedShelf] = useState(null);

  const handleFloorSizeChange = (newSize) => {
    updateStoreLayout({
      ...storeLayout,
      floorSize: newSize
    });
  };

  const handleAddShelf = () => {
    const newShelf = {
      id: `shelf-${Date.now()}`,
      position: [0, 0, 0],
      rotation: [0, 0, 0],
      size: [2, 1.8, 0.4],
      type: 'standard',
      items: []
    };
    addShelf(newShelf);
  };

  const handleShelfUpdate = (shelfId, updates) => {
    updateShelf(shelfId, updates);
  };

  const shelfTypes = [
    { value: 'standard', label: 'Standard Shelf' },
    { value: 'corner', label: 'Corner Unit' },
    { value: 'endcap', label: 'End Cap' },
    { value: 'gondola', label: 'Gondola' },
    { value: 'wall', label: 'Wall Mount' }
  ];

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 2, pb: 1 }}>
        <Typography variant="h6" gutterBottom>
          Store Layout
        </Typography>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', px: 2 }}>
        {/* Floor Settings */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <GridView />
              <Typography variant="subtitle2">Floor Settings</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ space: 2 }}>
              <Typography variant="body2" gutterBottom>
                Floor Size (meters)
              </Typography>
              <Slider
                value={storeLayout.floorSize || 20}
                onChange={(e, value) => handleFloorSizeChange(value)}
                min={10}
                max={50}
                step={5}
                marks
                valueLabelDisplay="auto"
                sx={{ mb: 2 }}
              />
              
              <Typography variant="caption" color="text.secondary">
                Current: {storeLayout.floorSize || 20}m × {storeLayout.floorSize || 20}m
              </Typography>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Shelving Units */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ViewModule />
              <Typography variant="subtitle2">
                Shelving Units ({storeLayout.shelves?.length || 0})
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <Box sx={{ p: 2, pt: 0 }}>
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={handleAddShelf}
                fullWidth
                sx={{ mb: 2 }}
              >
                Add Shelf Unit
              </Button>
            </Box>

            {storeLayout.shelves?.length > 0 ? (
              <List dense>
                {storeLayout.shelves.map((shelf, index) => (
                  <ListItem 
                    key={shelf.id} 
                    sx={{ 
                      borderBottom: 1, 
                      borderColor: 'divider',
                      cursor: 'pointer',
                      bgcolor: selectedShelf?.id === shelf.id ? 'action.selected' : 'transparent'
                    }}
                    onClick={() => setSelectedShelf(shelf)}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2">
                            Shelf {index + 1}
                          </Typography>
                          <Chip 
                            label={shelfTypes.find(t => t.value === shelf.type)?.label || shelf.type} 
                            size="small" 
                            variant="outlined"
                          />
                        </Box>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          Position: ({shelf.position[0].toFixed(1)}, {shelf.position[2].toFixed(1)}) • 
                          Items: {shelf.items?.length || 0}
                        </Typography>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeShelf(shelf.id);
                        }}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Store sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  No shelves added yet
                </Typography>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>

        {/* Selected Shelf Properties */}
        {selectedShelf && (
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Edit />
                <Typography variant="subtitle2">Shelf Properties</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ space: 2 }}>
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel>Shelf Type</InputLabel>
                  <Select
                    value={selectedShelf.type}
                    label="Shelf Type"
                    onChange={(e) => handleShelfUpdate(selectedShelf.id, { type: e.target.value })}
                  >
                    {shelfTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Typography variant="body2" gutterBottom>
                  Position
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <TextField
                    label="X"
                    type="number"
                    size="small"
                    value={selectedShelf.position[0]}
                    onChange={(e) => {
                      const newPos = [...selectedShelf.position];
                      newPos[0] = parseFloat(e.target.value) || 0;
                      handleShelfUpdate(selectedShelf.id, { position: newPos });
                    }}
                  />
                  <TextField
                    label="Z"
                    type="number"
                    size="small"
                    value={selectedShelf.position[2]}
                    onChange={(e) => {
                      const newPos = [...selectedShelf.position];
                      newPos[2] = parseFloat(e.target.value) || 0;
                      handleShelfUpdate(selectedShelf.id, { position: newPos });
                    }}
                  />
                </Box>

                <Typography variant="body2" gutterBottom>
                  Size (W × H × D)
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <TextField
                    label="Width"
                    type="number"
                    size="small"
                    value={selectedShelf.size[0]}
                    onChange={(e) => {
                      const newSize = [...selectedShelf.size];
                      newSize[0] = parseFloat(e.target.value) || 0.1;
                      handleShelfUpdate(selectedShelf.id, { size: newSize });
                    }}
                  />
                  <TextField
                    label="Height"
                    type="number"
                    size="small"
                    value={selectedShelf.size[1]}
                    onChange={(e) => {
                      const newSize = [...selectedShelf.size];
                      newSize[1] = parseFloat(e.target.value) || 0.1;
                      handleShelfUpdate(selectedShelf.id, { size: newSize });
                    }}
                  />
                  <TextField
                    label="Depth"
                    type="number"
                    size="small"
                    value={selectedShelf.size[2]}
                    onChange={(e) => {
                      const newSize = [...selectedShelf.size];
                      newSize[2] = parseFloat(e.target.value) || 0.1;
                      handleShelfUpdate(selectedShelf.id, { size: newSize });
                    }}
                  />
                </Box>

                <Typography variant="body2" gutterBottom>
                  Rotation (Y-axis)
                </Typography>
                <Slider
                  value={(selectedShelf.rotation[1] * 180) / Math.PI}
                  onChange={(e, value) => {
                    const newRot = [...selectedShelf.rotation];
                    newRot[1] = (value * Math.PI) / 180;
                    handleShelfUpdate(selectedShelf.id, { rotation: newRot });
                  }}
                  min={0}
                  max={360}
                  step={15}
                  marks
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => `${value}°`}
                />
              </Box>
            </AccordionDetails>
          </Accordion>
        )}
      </Box>
    </Box>
  );
};

export default StoreLayoutPanel;
